import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import { CheckCircle, Paperclip, RotateCcw, ShieldCheck } from 'lucide-react';

const STATUS_LABEL = {
  open: 'statusOpen',
  assigned: 'statusAssigned',
  in_progress: 'statusInProgress',
  pending_verification: 'statusPendingVerification',
  closed: 'statusClosed',
  dismissed: 'statusDismissed',
};

const STATUS_STYLE = {
  open: 'bg-gray-100 text-gray-800',
  assigned: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-amber-100 text-amber-800',
  pending_verification: 'bg-purple-100 text-purple-800',
  closed: 'bg-green-100 text-green-800',
  dismissed: 'bg-gray-100 text-gray-500',
};

export const CorrectiveActionWorkflow = ({ action, currentUserId, canVerify, onUpdated }) => {
  const { t } = useTranslation();
  const [completionNotes, setCompletionNotes] = useState('');
  const [verificationNotes, setVerificationNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const status = action.status || 'open';
  const isAssignee = action.assignedTo === currentUserId;
  const canComplete = !['closed', 'pending_verification', 'dismissed'].includes(status)
    && (isAssignee || canVerify);
  const awaitingVerification = status === 'pending_verification';
  const canAttachEvidence = !['closed', 'dismissed'].includes(status) && (isAssignee || canVerify);
  // The person who reported the correction cannot also sign it off
  const canSignOff = canVerify && awaitingVerification && action.completedBy !== currentUserId;

  const call = async (path, body, successKey, failKey) => {
    setBusy(true);
    try {
      const { data } = await axiosInstance.post(`/company/corrective-actions/${action.id}${path}`, body);
      toast.success(t(`correctiveWorkflow.${successKey}`));
      onUpdated?.(data);
      setCompletionNotes('');
      setVerificationNotes('');
    } catch (error) {
      toast.error(error.response?.data?.detail || t(`correctiveWorkflow.${failKey}`));
    } finally {
      setBusy(false);
    }
  };

  const uploadEvidence = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await axiosInstance.post(
        `/company/corrective-actions/${action.id}/evidence`, form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(t('correctiveWorkflow.evidenceAdded'));
      onUpdated?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('correctiveWorkflow.evidenceFailed'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="space-y-3 border-t pt-3" data-testid="corrective-action-workflow">
      <div className="flex flex-wrap items-center gap-2">
        <Badge className={STATUS_STYLE[status]} data-testid="corrective-action-status">
          {t(`correctiveWorkflow.${STATUS_LABEL[status] || 'statusOpen'}`)}
        </Badge>
        {action.isOverdue && (
          <Badge className="bg-red-100 text-red-800" data-testid="corrective-action-overdue">
            {t('correctiveWorkflow.overdue')}
          </Badge>
        )}
        {action.verifiedByName && (
          <span className="text-xs text-gray-500" data-testid="corrective-action-verified-by">
            {t('correctiveWorkflow.closedBy')} {action.verifiedByName}
          </span>
        )}
      </div>

      {awaitingVerification && (
        <p className="text-sm text-purple-700" data-testid="awaiting-verification-note">
          {t('correctiveWorkflow.awaitingVerificationBy')}
        </p>
      )}

      {awaitingVerification && action.completedBy === currentUserId && (
        <p className="text-sm text-amber-700" data-testid="self-approval-note">
          {t('correctiveWorkflow.selfApprovalNote')}
        </p>
      )}

      <div className="flex items-center gap-2">
        {canAttachEvidence && <input
          ref={fileRef}
          type="file"
          className="hidden"
          onChange={uploadEvidence}
          data-testid="evidence-file-input"
        />}
        {canAttachEvidence && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
            data-testid="attach-evidence-btn"
          >
            <Paperclip className="w-4 h-4 mr-1" />
            {uploading ? t('correctiveWorkflow.uploading') : t('correctiveWorkflow.addEvidence')}
          </Button>
        )}
        {(action.evidence?.length || 0) > 0 && (
          <span className="text-xs text-gray-500" data-testid="evidence-count">
            {t('correctiveWorkflow.evidence')}: {action.evidence.length}
          </span>
        )}
      </div>

      {canComplete && (
        <div className="space-y-2">
          <Label>{t('correctiveWorkflow.completionNotes')}</Label>
          <Textarea
            rows={2}
            value={completionNotes}
            onChange={(e) => setCompletionNotes(e.target.value)}
            data-testid="completion-notes-input"
          />
          <Button
            size="sm"
            disabled={busy || !completionNotes.trim()}
            onClick={() => call('/complete', { completionNotes }, 'completed', 'completeFailed')}
            data-testid="mark-complete-btn"
          >
            <CheckCircle className="w-4 h-4 mr-1" />
            {t('correctiveWorkflow.markComplete')}
          </Button>
        </div>
      )}

      {canSignOff && (
        <div className="space-y-2">
          <Label>{t('correctiveWorkflow.verificationNotes')}</Label>
          <Textarea
            rows={2}
            value={verificationNotes}
            onChange={(e) => setVerificationNotes(e.target.value)}
            data-testid="verification-notes-input"
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={busy}
              onClick={() => call('/verify', { approved: true, verificationNotes },
                'verified', 'verifyFailed')}
              data-testid="verify-action-btn"
            >
              <ShieldCheck className="w-4 h-4 mr-1" />
              {t('correctiveWorkflow.verify')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => {
                if (!verificationNotes.trim()) {
                  toast.error(t('correctiveWorkflow.verifyReasonRequired'));
                  return;
                }
                call('/verify', { approved: false, verificationNotes }, 'returned', 'verifyFailed');
              }}
              data-testid="reject-action-btn"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              {t('correctiveWorkflow.reject')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CorrectiveActionWorkflow;
